import { formatInTimeZone } from 'date-fns-tz';
import { supabase } from '../lib/supabaseClient';
import { expandSchedule, type ScheduleInput } from '../domain/positions';
import type { AjusteJornadas } from '../domain/ajusteJornadas';
import { getCampaign, listSchedules } from './campaigns';
import { completarTrabajadores } from './trabajadoresDeMisTiendas';
import type { PositionDbRow } from '../lib/db-types';

export interface GenerateResult {
  /** Posiciones realmente insertadas (las repetidas las descarta el ON CONFLICT del RPC). */
  inserted: number;
  /** Total de posiciones que produce la expansión de los horarios (incluye las ya existentes). */
  expanded: number;
}

/** Fila tal como la espera el RPC insert_positions (snake_case, igual a la tabla). */
interface PositionInsertRow {
  schedule_id: string;
  campaign_id: string;
  pdv_id: string;
  date: string;
  start_time: string;
  end_time: string;
  slot_index: number;
  rate_amount: number;
  rate_unit: 'jornada' | 'hora';
}

/**
 * Expande TODOS los horarios de la campaña dentro de [start_date, end_date] y los inserta
 * vía RPC `insert_positions` (ON CONFLICT (schedule_id, date, slot_index) DO NOTHING).
 * Es idempotente: correrla dos veces sobre la misma programación inserta 0 la segunda vez.
 */
export async function generatePositions(campaignId: string): Promise<GenerateResult> {
  const campaign = await getCampaign(campaignId);
  const schedules = await listSchedules(campaignId);

  const rows: PositionInsertRow[] = [];
  for (const s of schedules) {
    const input: ScheduleInput = {
      scheduleId: s.id,
      campaignId,
      pdvId: s.pdv_id,
      weekdays: s.weekdays,
      extraDates: s.extra_dates,
      // Sin la vigencia, «Generar posiciones» devolvería las jornadas de un horario dado de baja.
      vigenteDesde: s.vigente_desde,
      vigenteHasta: s.vigente_hasta,
      startTime: s.start_time.slice(0, 5),
      endTime: s.end_time.slice(0, 5),
      headcount: s.headcount,
      rateAmount: Number(s.rate_amount),
      rateUnit: s.rate_unit as 'jornada' | 'hora',
    };
    for (const p of expandSchedule(input, campaign.start_date, campaign.end_date)) {
      rows.push({
        schedule_id: p.scheduleId,
        campaign_id: p.campaignId,
        pdv_id: p.pdvId,
        date: p.date,
        start_time: p.startTime,
        end_time: p.endTime,
        slot_index: p.slotIndex,
        rate_amount: p.rateAmount,
        rate_unit: p.rateUnit,
      });
    }
  }

  if (rows.length === 0) return { inserted: 0, expanded: 0 };

  const { data, error } = await supabase.rpc('insert_positions', { rows });
  if (error) throw new Error(error.message);
  return { inserted: Number(data ?? 0), expanded: rows.length };
}

export interface PositionFilter {
  campaignId: string;
  dateFrom?: string;
  dateTo?: string;
  pdvId?: string;
  /** Estado de la posición. Lo usa el Grupo D para listar solo las `abierta`. */
  status?: 'abierta' | 'asignada' | 'cerrada';
}

export type PositionWithPdv = PositionDbRow & { pdv: { code: string; name: string } };

/**
 * FIRMA CANÓNICA: un ÚNICO parámetro objeto.
 * El Grupo D la consume como `listPositions({ campaignId, status: 'abierta' })`.
 */
export async function listPositions(filter: PositionFilter): Promise<PositionWithPdv[]> {
  let q = supabase
    .from('positions')
    .select('*, pdv:pdv_id(code, name)')
    .eq('campaign_id', filter.campaignId)
    .order('date')
    .order('start_time')
    .order('slot_index');
  if (filter.dateFrom) q = q.gte('date', filter.dateFrom);
  if (filter.dateTo) q = q.lte('date', filter.dateTo);
  if (filter.pdvId) q = q.eq('pdv_id', filter.pdvId);
  if (filter.status) q = q.eq('status', filter.status);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as PositionWithPdv[];
}

// ---------------------------------------------------------------------------
// Posiciones abiertas CON su asignación viva (pestaña "Posiciones abiertas")
//
// POR QUÉ EXISTE: `assign_position` crea la asignación como `propuesta` y **no toca
// `positions.status`** — la posición sigue 'abierta' hasta que el jefe la aprueba (ahí
// pasa a 'asignada'). Listando solo por estado, una posición ya asignada se ve IDÉNTICA
// a una libre: el coordinador no sabe si ya asignó esa fila, y si vuelve a intentarlo el
// servidor le responde "La posición ya está ocupada". Este lector trae, junto a cada
// posición abierta, la asignación viva que pueda tener y quién es.
// ---------------------------------------------------------------------------

/** Igual que en `api/assignments.ts`: PostgREST trunca en silencio a 1000 filas. */
const OPEN_PAGE_SIZE = 1000;

/**
 * El embed de `assignments` NO lleva `!inner`: las posiciones libres son justamente las
 * que no tienen asignación y con `!inner` desaparecerían. El filtro por estado limita las
 * asignaciones embebidas (una `reemplazada` o `rechazada` no ocupa la posición), no las
 * posiciones. Hint de FK obligatorio: `assignments` tiene tres FK a `profiles`.
 *
 * `worker_id` va aparte del embed porque el supervisor de ruta no lee `profiles` (0218): para él
 * `worker` llega en null y `completarTrabajadores` lo rellena buscando por ese id.
 */
const OPEN_SELECT =
  `id, date, start_time, end_time, slot_index,
   pdv:pdv_id(code, name),
   assignments(id, status, worker_id,
     worker:profiles!assignments_worker_id_fkey(id, dni, first_names, last_names))`;

export interface OpenPositionAssignment {
  id: string;
  status: 'propuesta' | 'aprobada';
  /**
   * Bajo RLS un embed sin permiso **no falla: devuelve null** (BITACORA §3.7). La
   * asignación existe igual, así que la fila se marca por la PRESENCIA de la asignación
   * y nunca por el nombre.
   */
  worker: { id: string; dni: string; firstNames: string; lastNames: string } | null;
}

export interface OpenPositionRow {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  slot_index: number;
  pdv: { code: string; name: string } | null;
  /** Asignación viva sobre la posición, o nulo si todavía está libre. */
  asignacion: OpenPositionAssignment | null;
}

export async function listOpenPositions(campaignId: string): Promise<OpenPositionRow[]> {
  type Raw = {
    id: string; date: string; start_time: string; end_time: string; slot_index: number;
    pdv: { code: string; name: string } | null;
    assignments: {
      id: string; status: string; worker_id: string | null;
      worker: { id: string; dni: string; first_names: string; last_names: string } | null;
    }[] | null;
  };
  const raw: Raw[] = [];
  let total: number | null = null;
  for (let from = 0; ; from += OPEN_PAGE_SIZE) {
    const { data, error, count } = await supabase
      .from('positions')
      .select(OPEN_SELECT, { count: 'exact' })
      .eq('campaign_id', campaignId)
      .eq('status', 'abierta')
      .in('assignments.status', ['propuesta', 'aprobada'])
      .order('date')
      .order('start_time')
      .order('slot_index')
      .order('id')
      .range(from, from + OPEN_PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    if (count !== null && count !== undefined) total = count;
    const batch = (data ?? []) as unknown as Raw[];
    raw.push(...batch);
    if (batch.length < OPEN_PAGE_SIZE) break;
  }
  if (total !== null && raw.length !== total) {
    throw new Error(
      `Lectura incompleta de posiciones abiertas: se recibieron ${raw.length} de ${total} filas. ` +
      'Vuelve a intentar: mostrar menos posiciones haría parecer cubiertas las que faltan.',
    );
  }
  // Muta las asignaciones: tiene que ir ANTES del `map`, que copia `worker` a otra forma.
  await completarTrabajadores(raw.flatMap((p) => p.assignments ?? []));

  return raw.map((p) => {
    const a = (p.assignments ?? [])[0] ?? null;
    return {
      id: p.id,
      date: p.date,
      start_time: p.start_time,
      end_time: p.end_time,
      slot_index: p.slot_index,
      pdv: p.pdv,
      asignacion: a
        ? {
            id: a.id,
            status: a.status as 'propuesta' | 'aprobada',
            worker: a.worker
              ? {
                  id: a.worker.id,
                  dni: a.worker.dni,
                  firstNames: a.worker.first_names,
                  lastNames: a.worker.last_names,
                }
              : null,
          }
        : null,
    };
  });
}

// ---------------------------------------------------------------------------
// Regeneración de un horario editado
//
// `insert_positions` usa ON CONFLICT DO NOTHING: si se edita el horario después de generar,
// las posiciones ya creadas conservan hora y tarifa viejas y el recurso ve en su celular un
// turno que ya no existe. Estas funciones sincronizan lo que se puede tocar sin romper nada
// (las `abierta`) y dejan las `asignada`/`cerrada` a decisión explícita del coordinador.
// ---------------------------------------------------------------------------

export interface StalePosition {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  rateAmount: number;
  rateUnit: 'jornada' | 'hora';
  status: 'asignada' | 'cerrada';
}

export interface ScheduleRefreshPreview {
  /** Posiciones `abierta` que se pondrán al día (hora, tarifa y PDV del horario actual). */
  updatable: number;
  /** Posiciones `abierta` en fechas que el horario ya no cubre: se eliminarán. */
  obsolete: number;
  /** Posiciones que faltan por crear con la programación actual. */
  missing: number;
  /** Posiciones con recurso (`asignada`/`cerrada`) que quedaron desfasadas del horario. */
  locked: StalePosition[];
}

/** Ver `AjusteJornadas`: qué pasó con cada jornada del horario. */
export type ScheduleRefreshResult = AjusteJornadas;

interface ScheduleRowFull {
  id: string;
  campaign_id: string;
  pdv_id: string;
  weekdays: number[];
  extra_dates: string[];
  vigente_desde: string | null;
  vigente_hasta: string | null;
  start_time: string;
  end_time: string;
  headcount: number;
  rate_amount: number | string;
  rate_unit: 'jornada' | 'hora';
}

const posKey = (date: string, slotIndex: number): string => `${date}#${slotIndex}`;
const hhmm = (t: string): string => t.slice(0, 5);

/** Carga el horario, expande lo que DEBERÍA existir hoy y lee lo que existe en la BD. */
async function loadRefreshContext(scheduleId: string): Promise<{
  schedule: ScheduleRowFull;
  expected: Map<string, PositionInsertRow>;
  current: PositionDbRow[];
}> {
  const { data: sched, error: schedError } = await supabase
    .from('campaign_schedules')
    // `extra_dates` NO es opcional acá: sin ella, las posiciones de fechas sueltas saldrían como
    // obsoletas y «Regenerar» las borraría. La vigencia tampoco: sin ella, «Regenerar» volvería a
    // crear las jornadas de un horario dado de baja (el servidor igual las descarta, 0304).
    .select('id, campaign_id, pdv_id, weekdays, extra_dates, vigente_desde, vigente_hasta, start_time, end_time, headcount, rate_amount, rate_unit')
    .eq('id', scheduleId)
    .single();
  if (schedError) throw new Error(schedError.message);
  const schedule = sched as unknown as ScheduleRowFull;

  const campaign = await getCampaign(schedule.campaign_id);
  const input: ScheduleInput = {
    scheduleId: schedule.id,
    campaignId: schedule.campaign_id,
    pdvId: schedule.pdv_id,
    weekdays: schedule.weekdays,
    extraDates: schedule.extra_dates,
    vigenteDesde: schedule.vigente_desde,
    vigenteHasta: schedule.vigente_hasta,
    startTime: hhmm(schedule.start_time),
    endTime: hhmm(schedule.end_time),
    headcount: schedule.headcount,
    rateAmount: Number(schedule.rate_amount),
    rateUnit: schedule.rate_unit,
  };

  const expected = new Map<string, PositionInsertRow>();
  for (const p of expandSchedule(input, campaign.start_date, campaign.end_date)) {
    expected.set(posKey(p.date, p.slotIndex), {
      schedule_id: p.scheduleId,
      campaign_id: p.campaignId,
      pdv_id: p.pdvId,
      date: p.date,
      start_time: p.startTime,
      end_time: p.endTime,
      slot_index: p.slotIndex,
      rate_amount: p.rateAmount,
      rate_unit: p.rateUnit,
    });
  }

  const { data: rows, error: rowsError } = await supabase
    .from('positions')
    .select('*')
    .eq('schedule_id', scheduleId)
    .order('date')
    .order('slot_index');
  if (rowsError) throw new Error(rowsError.message);

  return { schedule, expected, current: (rows ?? []) as PositionDbRow[] };
}

/** ¿La posición guardada difiere de lo que dice el horario hoy? */
function isStale(p: PositionDbRow, s: ScheduleRowFull): boolean {
  return hhmm(p.start_time) !== hhmm(s.start_time)
    || hhmm(p.end_time) !== hhmm(s.end_time)
    || Number(p.rate_amount) !== Number(s.rate_amount)
    || p.rate_unit !== s.rate_unit
    || p.pdv_id !== s.pdv_id;
}

export async function previewScheduleRefresh(scheduleId: string): Promise<ScheduleRefreshPreview> {
  const { schedule, expected, current } = await loadRefreshContext(scheduleId);
  let updatable = 0;
  let obsolete = 0;
  const locked: StalePosition[] = [];
  const present = new Set<string>();

  for (const p of current) {
    const key = posKey(p.date, p.slot_index);
    present.add(key);
    const stillExpected = expected.has(key);
    if (p.status === 'abierta') {
      if (!stillExpected) obsolete += 1;
      else if (isStale(p, schedule)) updatable += 1;
    } else if (!stillExpected || isStale(p, schedule)) {
      locked.push({
        id: p.id,
        date: p.date,
        startTime: hhmm(p.start_time),
        endTime: hhmm(p.end_time),
        rateAmount: Number(p.rate_amount),
        rateUnit: p.rate_unit as 'jornada' | 'hora',
        status: p.status as 'asignada' | 'cerrada',
      });
    }
  }

  let missing = 0;
  for (const key of expected.keys()) if (!present.has(key)) missing += 1;

  return { updatable, obsolete, missing, locked };
}

/**
 * Deja las jornadas del horario iguales a lo que el horario dice. Corre en «Regenerar» y, desde la 0325, cada vez
 * que se guarda un horario: el 21-oct Roberto cambió los de Pringles a fechas puntuales y las 63 jornadas viejas
 * siguieron en Asignaciones, porque editar no las tocaba y «Generar posiciones» solo agrega.
 *
 * Las que tienen persona nunca se tocan: se cuentan para avisar. `motivo` queda escrito en `positions_borradas`.
 */
export async function refreshSchedulePositions(
  scheduleId: string,
  motivo = 'Regenerar horario: el horario ya no pide esta jornada',
): Promise<ScheduleRefreshResult> {
  const { schedule, expected, current } = await loadRefreshContext(scheduleId);

  // 1) Quitar las `abierta` que el horario ya no pide. Por el servidor (0325) y NO con un DELETE directo: borra solo
  //    las que no tienen ninguna asignación —con una postulación rechazada en el lote, la clave foránea tumbaba el
  //    borrado entero— y archiva cada una con quién y por qué.
  const obsoleteIds = current
    .filter((p) => p.status === 'abierta' && !expected.has(posKey(p.date, p.slot_index)))
    .map((p) => p.id);
  let deleted = 0;
  let kept = 0;
  if (obsoleteIds.length > 0) {
    const { data, error } = await supabase.rpc('borrar_jornadas_sin_persona', {
      p_ids: obsoleteIds,
      p_motivo: motivo,
    });
    if (error) throw new Error(error.message);
    const r = (data ?? {}) as { borradas?: number; conservadas?: number };
    deleted = Number(r.borradas ?? 0);
    kept = Number(r.conservadas ?? 0);
  }

  // 2) Poner al día las `abierta` que siguen vigentes (hora, tarifa y PDV del horario actual). Se cuentan solo las
  //    que de verdad cambiaban: el aviso de «puestas al día» no puede decir 9 cuando no cambió ninguna.
  const updated = current.filter(
    (p) => p.status === 'abierta' && expected.has(posKey(p.date, p.slot_index)) && isStale(p, schedule),
  ).length;
  if (updated > 0) {
    const { error: updateError } = await supabase
      .from('positions')
      .update({
        pdv_id: schedule.pdv_id,
        start_time: hhmm(schedule.start_time),
        end_time: hhmm(schedule.end_time),
        rate_amount: Number(schedule.rate_amount),
        rate_unit: schedule.rate_unit,
      })
      .eq('schedule_id', scheduleId)
      .eq('status', 'abierta')
      .select('id');
    if (updateError) throw new Error(updateError.message);
  }

  // 3) Crear las que falten (el RPC ignora las que ya existen)
  let inserted = 0;
  const rows = Array.from(expected.values());
  if (rows.length > 0) {
    const { data, error } = await supabase.rpc('insert_positions', { rows });
    if (error) throw new Error(error.message);
    inserted = Number(data ?? 0);
  }

  // 4) Las que tienen recurso NO se tocan: se devuelven contadas para avisar en pantalla
  const locked = current.filter(
    (p) => p.status !== 'abierta'
      && (!expected.has(posKey(p.date, p.slot_index)) || isStale(p, schedule)),
  ).length;

  return { updated, deleted, inserted, locked, kept };
}

// --- Faltas (Task 30): posiciones aprobadas de fecha pasada sin ninguna attendance ---

/** Igual que en `api/attendance.ts`: PostgREST trunca en silencio a 1000 filas. */
const PAGE_SIZE = 1000;

export interface AbsenceRow {
  key: string;
  /**
   * La asignación del turno sin marcar. Es lo que permite COMPLETARLO a mano (0076): sin ella la
   * pantalla sabe que falta pero no tiene dónde escribir, porque `attendance.assignment_id` es
   * obligatorio y la fecha, el PDV y el trabajador salen de ahí.
   */
  assignment_id: string;
  date: string;
  start_time: string;
  end_time: string;
  /** Para filtrar por supervisor en Validación: el reparto es por tienda (`campaign_pdv_supervisors`). */
  pdvId?: string;
  pdv: { name: string } | null;
  worker: { id: string; dni: string; first_names: string; last_names: string } | null;
}

export async function listAbsences(
  campaignId: string, dateFrom: string, dateTo: string,
): Promise<AbsenceRow[]> {
  const todayLima = formatInTimeZone(new Date(), 'America/Lima', 'yyyy-MM-dd');
  type Raw = {
    id: string; date: string; start_time: string; end_time: string; pdv_id: string;
    pdv: { name: string } | null;
    assignments: {
      id: string;
      worker_id: string | null;
      worker: { id: string; dni: string; first_names: string; last_names: string } | null;
      attendance: { id: string }[];
    }[];
  };
  const raw: Raw[] = [];
  let total: number | null = null;
  for (let from = 0; ; from += PAGE_SIZE) {
    // Hint de FK obligatorio: `assignments` tiene tres FK a `profiles`.
    const { data, error, count } = await supabase
      .from('positions')
      .select(
        `id, date, start_time, end_time, pdv_id, pdv:pdv(name),
        assignments!inner(id, status, worker_id,
          worker:profiles!assignments_worker_id_fkey(id, dni, first_names, last_names),
          attendance(id))`,
        { count: 'exact' },
      )
      .eq('campaign_id', campaignId)
      .eq('assignments.status', 'aprobada')
      .gte('date', dateFrom)
      .lte('date', dateTo)
      .lt('date', todayLima)
      .order('date')
      .order('id')
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    if (count !== null) total = count;
    const batch = (data ?? []) as unknown as Raw[];
    raw.push(...batch);
    if (batch.length < PAGE_SIZE) break;
  }
  if (total !== null && raw.length !== total) {
    throw new Error(
      `Lectura incompleta de posiciones: se recibieron ${raw.length} de ${total} filas. ` +
      'Reduce el rango de fechas y vuelve a intentar.',
    );
  }
  // El supervisor de ruta no lee `profiles` (0218): su `worker` llega en null y se rellena acá,
  // por `worker_id`, antes de copiarlo a las filas.
  await completarTrabajadores(raw.flatMap((p) => p.assignments ?? []));
  return raw.flatMap((p) =>
    p.assignments
      .filter((a) => (a.attendance ?? []).length === 0)
      .map((a) => ({
        key: `${p.id}:${a.id}`,
        assignment_id: a.id,
        date: p.date,
        start_time: p.start_time,
        end_time: p.end_time,
        pdvId: p.pdv_id,
        pdv: p.pdv,
        worker: a.worker,
      })),
  );
}

// ---------------------------------------------------------------------------
// Turnos para el reporte de Validación: UNA fila por turno, no por marcado
// ---------------------------------------------------------------------------

/** Una de las dos marcas del turno, con todo lo que el Excel muestra de ella. */
export interface TurnoMarca {
  id: string;
  device_ts: string;
  flags: string[];
  status: 'pendiente' | 'validada' | 'observada';
  validation_note: string | null;
  lat: number | null;
  lng: number | null;
  photo_path: string;
}

export interface TurnoRow {
  key: string;
  date: string;
  start_time: string;
  end_time: string;
  /** Para filtrar por supervisor en Validación. */
  pdvId?: string;
  pdv: { name: string } | null;
  /**
   * Id de la asignación aprobada, o null si la posición no tiene ninguna.
   *
   * Es lo único que separa "estaba programado y nadie fue" (falta: hay a quién preguntarle) de
   * "nunca se cubrió" (hueco de cobertura: no hay a quién preguntarle). Se mira el ID y **nunca**
   * `worker`, porque bajo RLS un embed sin permiso no falla, devuelve null (BITACORA §3.7): un
   * turno cubierto por alguien que este usuario no puede leer saldría reportado como "Sin cubrir"
   * y el cliente vería un hueco donde sí hubo gente.
   */
  assignmentId: string | null;
  worker: { id: string; dni: string; first_names: string; last_names: string } | null;
  ingreso: TurnoMarca | null;
  salida: TurnoMarca | null;
}

interface RawTurnoAttendance {
  id: string;
  kind: 'ingreso' | 'salida';
  device_ts: string;
  flags: string[] | null;
  status: 'pendiente' | 'validada' | 'observada';
  validation_note: string | null;
  lat: number | null;
  lng: number | null;
  photo_path: string | null;
}

/**
 * Normaliza una marca cruda al tipo que consume el Excel.
 *
 * Los `??` de abajo NO cubren un caso real de la base: en `attendance` (0001_schema.sql) `flags`
 * es `text[] not null default '{}'` y `photo_path` es `text not null`, así que ninguno de los dos
 * llega nulo. Están porque el tipo crudo los declara opcionales —PostgREST no traslada el NOT NULL
 * a la respuesta— y el Excel necesita un arreglo y una cadena, nunca `null`. Son inocuos y se
 * dejan; lo que no se puede dejar es afirmar que la BD devuelve nulos, porque el siguiente que lo
 * lea va a ir a buscar la defensa equivalente en los demás lectores de `attendance`.
 */
function toTurnoMarca(m: RawTurnoAttendance | undefined): TurnoMarca | null {
  if (m === undefined) return null;
  return {
    id: m.id,
    device_ts: m.device_ts,
    flags: m.flags ?? [],
    status: m.status,
    validation_note: m.validation_note,
    lat: m.lat,
    lng: m.lng,
    photo_path: m.photo_path ?? '',
  };
}

/**
 * Toda la programación del rango, una fila por TURNO, para el Excel de Validación.
 *
 * No se reusa `listAbsences` aunque desde afuera se parezcan: aquella usa `assignments!inner`
 * (borra del resultado las posiciones sin cubrir, que son justo los huecos que este reporte
 * existe para enseñar), corta en `.lt(hoy)` (deja fuera el día en curso) y solo devuelve las
 * asignaciones con CERO marcados. Este reporte se usa como plantilla de programación: tienen que
 * salir todos los turnos del rango, cumplidos o no, y con lo que se marcó en cada uno.
 *
 * Emparejar ingreso con salida no es ambiguo nunca: `attendance` tiene UNIQUE(assignment_id,
 * kind), o sea que por asignación hay como mucho UN ingreso y UNA salida.
 */
export async function listTurnosParaReporte(
  campaignId: string, dateFrom: string, dateTo: string,
): Promise<TurnoRow[]> {
  type Raw = {
    id: string; date: string; start_time: string; end_time: string; pdv_id: string;
    pdv: { name: string } | null;
    assignments: {
      id: string; status: string; worker_id: string | null;
      worker: { id: string; dni: string; first_names: string; last_names: string } | null;
      attendance: RawTurnoAttendance[] | null;
    }[] | null;
  };
  const raw: Raw[] = [];
  let total: number | null = null;
  for (let from = 0; ; from += PAGE_SIZE) {
    // El embed de `assignments` va SIN `!inner`: una posición sin cubrir no tiene ninguna y con
    // `!inner` desaparecería. Hint de FK obligatorio: `assignments` tiene TRES FK a `profiles`
    // (worker_id, assigned_by, approved_by) y sin él PostgREST responde 300.
    const { data, error, count } = await supabase
      .from('positions')
      .select(
        `id, date, start_time, end_time, pdv_id, pdv:pdv(name),
        assignments(id, status, worker_id,
          worker:profiles!assignments_worker_id_fkey(id, dni, first_names, last_names),
          attendance(id, kind, device_ts, flags, status, validation_note, lat, lng, photo_path))`,
        { count: 'exact' },
      )
      .eq('campaign_id', campaignId)
      .gte('date', dateFrom)
      .lte('date', dateTo)
      .order('date')
      .order('id') // desempate estable: sin él dos páginas podrían repetir u omitir filas
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    if (count !== null) total = count;
    const batch = (data ?? []) as unknown as Raw[];
    raw.push(...batch);
    if (batch.length < PAGE_SIZE) break;
  }
  // PostgREST corta la respuesta en 1000 filas SIN avisar. Un reporte de asistencia truncado no
  // se ve roto: se ve como un mes con menos turnos, y ese mes se paga de menos. Antes de entregar
  // un archivo incompleto es preferible fallar y que el usuario reintente.
  if (total !== null && raw.length !== total) {
    throw new Error(
      `Lectura incompleta de turnos: se recibieron ${raw.length} de ${total} posiciones. ` +
      'Reduce el rango de fechas y vuelve a intentar: un reporte con turnos de menos se paga de menos.',
    );
  }
  // El supervisor de ruta no lee `profiles` (0218): su `worker` llega en null y se rellena acá,
  // por `worker_id`, antes de copiarlo a las filas.
  await completarTrabajadores(raw.flatMap((p) => p.assignments ?? []));

  return raw.flatMap((p): TurnoRow[] => {
    // El estado de la asignación se filtra ACÁ y no en la consulta a propósito. Un
    // `.eq('assignments.status', 'aprobada')` sobre una relación embebida SIN `!inner` no filtra
    // lo que uno cree —según la versión de PostgREST puede recortar las posiciones y no solo las
    // asignaciones embebidas— y perder una posición sin cubrir sería perder justo el hueco de
    // cobertura que este reporte existe para mostrar. En JS el filtro es explícito y se prueba.
    const aprobadas = (p.assignments ?? []).filter((a) => a.status === 'aprobada');
    const base = {
      date: p.date, start_time: p.start_time, end_time: p.end_time, pdvId: p.pdv_id, pdv: p.pdv,
    };
    if (aprobadas.length === 0) {
      // Sin recurso asignado igual se devuelve UNA fila: que nadie cubriera ese turno es un dato,
      // no la ausencia de uno. Sin esta fila el reporte diría que ese día esa tienda no estaba
      // programada, que es exactamente lo contrario de lo que pasó.
      return [{
        ...base, key: `${p.id}:sin-asignacion`,
        assignmentId: null, worker: null, ingreso: null, salida: null,
      }];
    }
    // Dos aprobadas a la vez sobre la misma posición NO existen: el índice único parcial
    // `assignments_una_viva_por_posicion_idx` (0001_schema.sql) es
    // `unique (position_id) where status in ('propuesta','aprobada')`. El `map` recorre a lo más
    // un elemento; se deja como `map` porque el filtro por estado vive en JS y no en la consulta.
    //
    // Lo que SÍ pasa y hay que saber leer en el archivo: `replace_assignment` marca la asignación
    // vieja como `reemplazada` y crea la nueva en `propuesta`. Entre ese momento y
    // `approve_assignments` la posición no tiene NINGUNA aprobada, así que ese turno sale
    // «Sin cubrir» aunque el reemplazo ya esté en marcha. No es un error del reporte —hasta que
    // el jefe aprueba, nadie está comprometido con ese turno—, pero explica por qué un turno que
    // el coordinador ya resolvió aparece como hueco de cobertura.
    return aprobadas.map((a) => {
      const marcas = a.attendance ?? [];
      return {
        ...base, key: `${p.id}:${a.id}`,
        assignmentId: a.id,
        worker: a.worker,
        ingreso: toTurnoMarca(marcas.find((m) => m.kind === 'ingreso')),
        salida: toTurnoMarca(marcas.find((m) => m.kind === 'salida')),
      };
    });
  });
}

// --- Vista del día (Task 34) ---

export interface DayBoardRow {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  status: string;
  pdv: { id: string; name: string; district: string | null } | null;
  campaign: { id: string; client: string; brand: string } | null;
  assignments: {
    id: string;
    status: string;
    /**
     * Va aparte del embed: el supervisor de ruta no lee `profiles` (0218), su `worker` llega en
     * null y `completarTrabajadores` lo rellena por este id.
     */
    worker_id?: string | null;
    worker: { id: string; dni: string; first_names: string; last_names: string } | null;
    attendance: {
      kind: 'ingreso' | 'salida';
      flags: string[];
      status: string;
      /** La hora que declaró el dispositivo al marcar; `created_at` es cuando llegó. */
      device_ts: string | null;
      created_at: string;
    }[];
  }[];
}

/**
 * Todas las posiciones de una fecha con su PDV, campaña, asignación y marcados.
 * Pagina con `.range()` (reutiliza el `PAGE_SIZE` declarado arriba para `listAbsences`):
 * con 50–200 recursos en varias campañas, un solo día puede superar el tope de 1000
 * filas de PostgREST y el tablero mostraría menos posiciones de las que hay.
 */
export async function getDayBoard(dateISO: string): Promise<DayBoardRow[]> {
  const rows: DayBoardRow[] = [];
  let total: number | null = null;
  for (let from = 0; ; from += PAGE_SIZE) {
    // Hint de FK obligatorio: `assignments` tiene tres FK a `profiles`.
    const { data, error, count } = await supabase
      .from('positions')
      .select(
        `id, date, start_time, end_time, status,
        pdv:pdv(id, name, district),
        campaign:campaigns!inner(id, client, brand),
        assignments(id, status, worker_id,
          worker:profiles!assignments_worker_id_fkey(id, dni, first_names, last_names),
          attendance(kind, flags, status, device_ts, created_at))`,
        { count: 'exact' },
      )
      .eq('date', dateISO)
      // Las campañas archivadas no aparecen en el tablero del día: si no, esconderlas del panel
      // sería una ilusión, porque el día sigue mostrando sus puestos.
      .is('campaign.archived_at', null)
      // 0041: igual que en Planificación. Un turno de ensayo en el tablero de Hoy sumaría a
      // "Posiciones de hoy" y movería el porcentaje del día, que es con lo que se gobierna
      // la operación.
      .eq('campaign.es_banco_pruebas', false)
      .order('start_time')
      .order('id')
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    if (count !== null) total = count;
    const batch = (data ?? []) as unknown as DayBoardRow[];
    rows.push(...batch);
    if (batch.length < PAGE_SIZE) break;
  }
  if (total !== null && rows.length !== total) {
    throw new Error(
      `Lectura incompleta del tablero: se recibieron ${rows.length} de ${total} posiciones.`,
    );
  }
  await completarTrabajadores(rows.flatMap((r) => r.assignments ?? []));
  return rows;
}

/**
 * Cambia la tarifa de UNA jornada (un día, una tienda), con constancia en auditoría.
 *
 * Va por RPC y no por `update` directo porque la tarifa es el número por el que se le paga a
 * una persona: hace falta saber quién la cambió, desde cuánto y por qué. La RPC además rechaza
 * tocar una jornada que ya está dentro de un cierre aprobado — eso se corrige por ajuste, no
 * reescribiendo la programación (0033).
 */
export async function setPositionRate(
  positionId: string,
  rate: number,
  unit: 'jornada' | 'hora',
  motivo: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('set_position_rate', {
    p_position: positionId, p_rate: rate, p_unit: unit, p_motivo: motivo,
  });
  if (error) throw new Error(error.message);
}
